import { c as _c } from "react/compiler-runtime";
import { Card } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import type { ReactNode, Ref } from 'react';
type PinnedCardProps = {
  children: ReactNode;
  /** Card's own header row. */
  header?: ReactNode;
  /** Row pinned to the card's bottom edge, below a full-width divider — the running-agents
   *  indicator grouped under the pinned content. */
  footer?: ReactNode;
  /** Extra classes for the inner card — padding and layout differ between the cards. */
  className?: string;
  /**
   * Only a card with a scrollable body may shrink below its content when the stack hits the
   * panel bound — pass the expand state. Left off, `min-height: auto` floors the card at its
   * content height, so the squeeze can never eat a collapsed card's header. A shrinkable card
   * floors at `min-h-14` rather than zero, so its header survives a full squeeze.
   */
  shrinkable?: boolean;
  ref?: Ref<HTMLDivElement>;
};

/**
 * Shared chrome for the cards pinned above the composer (journey progress, plan, question):
 * a Card ground around its white body, so the stack reads as one unit. Both
 * wrappers are flex columns because the stack's height bound only reaches the scrollable body
 * if every level between them is a shrinkable flex column.
 */
const PinnedCard = t0 => {
  const $ = _c(18);
  const {
    children,
    header,
    footer,
    className,
    shrinkable,
    ref
  } = t0;
  let t1;
  if ($[0] !== shrinkable) {
    t1 = cx("flex flex-col", {
      "min-h-14": shrinkable
    });
    $[0] = shrinkable;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== className || $[3] !== shrinkable) {
    t2 = cx("p-3 flex flex-col", className, {
      "min-h-0": shrinkable
    });
    $[2] = className;
    $[3] = shrinkable;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== header) {
    t3 = header && <div className="shrink-0 flex items-center gap-2 w-full max-w-full">{header}</div>;
    $[5] = header;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== footer) {
    t4 = footer && <><div aria-hidden={true} className="shrink-0 -mx-3 mt-3 border-t border-solid border-tertiary" />{footer}</>;
    $[7] = footer;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== children || $[10] !== t2 || $[11] !== t3 || $[12] !== t4) {
    t5 = <Card.Body className={t2}>{t3}{children}{t4}</Card.Body>;
    $[9] = children;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== ref || $[15] !== t1 || $[16] !== t5) {
    t6 = <Card.Root ref={ref} data-pinned-card={true} className={t1}>{t5}</Card.Root>;
    $[14] = ref;
    $[15] = t1;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
};
export default PinnedCard;